import { useId, useRef } from "react";

export const CONTEXT_SELECTIONS = ["professional", "personal", "both"];

const OPTIONS = {
  professional: {
    label: "Professional",
    description: "Work, school, and career relationships.",
  },
  personal: {
    label: "Personal",
    description: "Friends, family, and life outside work.",
  },
  both: {
    label: "Both",
    description: "Keep separate details and map paths for each side.",
  },
};

/** Select which facet or facets a person should have when adding them. */
export default function ContextSelector({
  value = "professional",
  onChange,
  label = "Where does this person belong?",
  description = "You can change this later.",
  disabled = false,
  className = "",
}) {
  const generatedId = useId();
  const labelId = `${generatedId}-label`;
  const descriptionId = `${generatedId}-description`;
  const buttonsRef = useRef([]);
  const selected = CONTEXT_SELECTIONS.includes(value) ? value : "professional";

  const choose = (index) => {
    if (disabled) return;
    const next = CONTEXT_SELECTIONS[index];
    onChange?.(next);
    buttonsRef.current[index]?.focus();
  };

  const handleKeyDown = (event, index) => {
    let nextIndex = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (index + 1) % CONTEXT_SELECTIONS.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (index - 1 + CONTEXT_SELECTIONS.length) % CONTEXT_SELECTIONS.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = CONTEXT_SELECTIONS.length - 1;
    }

    if (nextIndex !== null) {
      event.preventDefault();
      choose(nextIndex);
    }
  };

  return (
    <div className={`context-selector ${className}`.trim()}>
      <div className="context-selector-heading" id={labelId}>{label}</div>
      {description && (
        <p className="context-selector-description" id={descriptionId}>{description}</p>
      )}
      <div
        className="context-selector-options"
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={description ? descriptionId : undefined}
        aria-disabled={disabled || undefined}
      >
        {CONTEXT_SELECTIONS.map((context, index) => {
          const option = OPTIONS[context];
          const isSelected = selected === context;
          return (
            <button
              key={context}
              ref={(node) => { buttonsRef.current[index] = node; }}
              type="button"
              role="radio"
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
              className={`context-selector-option context-${context}${isSelected ? " is-active" : ""}`}
              onClick={() => choose(index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              disabled={disabled}
            >
              <span className="context-selector-mark" aria-hidden="true" />
              <span className="context-selector-copy">
                <span className="context-selector-option-label">{option.label}</span>
                <span className="context-selector-option-description">{option.description}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

